import { useI18n, useNuxtApp, useRuntimeConfig } from '#imports'
import { computed, type ComputedRef } from 'vue'
import { instantMeiliSearch, type InstantMeiliSearchInstance } from '@meilisearch/instant-meilisearch'
import { createMeiliSearchHttpClient } from '~/utils/meiliHttpClient'

export type UseMeiliSearchReturn = {
    searchIndex: ComputedRef<string | null>
    meiliSearchClient: ComputedRef<InstantMeiliSearchInstance | null>
}

/**
 * What the A4G Search API results page needs to run a query:
 *
 * - `searchIndex`: `products-<country>-<language>` — country from the app's base URL,
 *   language the active i18n locale taken as-is, no mapping table.
 * - `meiliSearchClient`: the `@meilisearch/instant-meilisearch` client, or `null` while the
 *   runtime config is unset. Its `hostUrl`/`apiKey` stay empty on purpose: the custom
 *   `httpClient` is the single seam carrying base URL and bearer key — and the one place
 *   that answers a query-less `multi-search` without calling the API.
 */
export function useMeiliSearch(): UseMeiliSearchReturn {
    const { $config } = useNuxtApp()
    const { locale } = useI18n()
    const config = useRuntimeConfig()

    const searchIndex = computed<string | null>(() => {
        let country = $config?.app?.baseURL as string | null | undefined

        if (country == null || country === '/') {
            country = 'de'
        } else {
            country = country.replace('/', '')
        }

        return `products-${country}-${locale.value}`
    })

    const meiliSearchClient = computed<InstantMeiliSearchInstance | null>(() => {
        const baseUrl = config.public.searchApiUrl as string | undefined
        const apiKey = config.public.searchApiKey as string | undefined

        if (baseUrl == null || baseUrl === '' || apiKey == null || apiKey === '') {
            return null
        }

        return instantMeiliSearch(baseUrl, '', {
            httpClient: createMeiliSearchHttpClient({ baseUrl, apiKey }),
            // Must stay false. True makes the adapter emit page/hitsPerPage, which
            // meiliRequestAdapter's ALLOWED_QUERY_FIELDS filters out — paging then breaks
            // silently. Flip only together with that list, once the backend supports it.
            finitePagination: false,
        }).searchClient
    })

    return { searchIndex, meiliSearchClient }
}
